/* eslint-disable sonarjs/no-duplicate-string */
import {
  CustomStructureAppearanceType,
  defaultCustomStructureAppearance,
  EventSummary,
  EventWithCustomField,
  ExhibitorProfilesPackNumber,
  ExponentStructureType,
  MinimalEventForParticipation,
  StructureType,
} from "@ac-expo/api-types";
import {
  EventScheduleType,
  formatRGBColor,
  getName,
  isNullish,
} from "@ac-expo/utils";
import {
  ArrowLeftIcon,
  EnvelopeSimpleIcon,
  HeartIcon,
  PhoneIcon,
  ShareFatIcon,
  UserIcon,
} from "@phosphor-icons/react";
import { File, FileType } from "@prisma/client";
import { noop } from "lodash";
import { FC, useEffect, useState } from "react";
import { cn } from "../../../utils";
import { PlanningEvent } from "../planning";
import { StructureContactSection } from "./StructureContactSection";
import { StructureDetailsSection } from "./StructureDetailsSection";
import { StructureNameSection } from "./StructureNameSection";

// Define the condensed structure type
export type CondensedStructureType = Pick<
  StructureType,
  | "id"
  | "businessName"
  | "businessSectorRelation"
  | "socials"
  | "acceptedTerms"
  | "phoneNumber"
  | "businessEmail"
  | "address"
  | "activities"
  | "products"
  | "documents"
  | "bannerPicture"
  | "picture"
  | "description"
>;

const structurePanelValues = {
  information: "participation",
  program: "product",
  exponents: "animation",
  documents: "document",
} as const;
export type StructurePanel =
  (typeof structurePanelValues)[keyof typeof structurePanelValues];

export const isStructurePanel = (panel: string): panel is StructurePanel =>
  Object.values(structurePanelValues).includes(panel as StructurePanel);

type StructureDetailsProps = {
  structureImageUrl?: string;
  structure: CondensedStructureType | ExponentStructureType;
  onWebsiteClick?: () => void;
  onSocialsClick?: () => void;
  onConversationClick: () => void;
  isLiked?: boolean;
  toggleLike?: () => void;
  participations:
    | EventWithCustomField<EventSummary>[]
    | MinimalEventForParticipation[];
  onPanelChange?: (to: StructurePanel) => void;
  activePanel?: StructurePanel;
  customAppearance?: CustomStructureAppearanceType | null;
  isEmbedded?: boolean;
  getFileUrl: (key?: string, type?: FileType) => string;
  onShareClick?: () => void;
  onBackClick?: () => void;
  onOpenActivityList?: (planningEvent: PlanningEvent[]) => void;
  onEventCardClick?: (eventId: string, eventSlug: string | null) => void;
  eventSchedules?: EventScheduleType[];
  exhibitorPackNumber?: ExhibitorProfilesPackNumber | null;
  actionGuard?: (
    action: () => void,
    fallback?: () => void,
    ignoreModal?: boolean
  ) => void;
  isBackgroundFixed?: boolean;
};

const StructureDetails: FC<StructureDetailsProps> = ({
  structure,
  onConversationClick = noop,
  onSocialsClick,
  onWebsiteClick,
  isLiked,
  toggleLike,
  participations,
  activePanel,
  onPanelChange,
  customAppearance,
  isEmbedded,
  getFileUrl,
  onShareClick,
  onBackClick = noop,
  onOpenActivityList = noop,
  onEventCardClick = noop,
  eventSchedules,
  exhibitorPackNumber,
  actionGuard,
  isBackgroundFixed = true,
}) => {
  const { bannerPicture, picture, description } = structure;
  const canShowBanner =
    exhibitorPackNumber === undefined ||
    (exhibitorPackNumber !== null && exhibitorPackNumber >= 2);

  if (!customAppearance) {
    customAppearance = defaultCustomStructureAppearance;
  }

  const canShowInterlocutors =
    exhibitorPackNumber === undefined ||
    (exhibitorPackNumber !== null && exhibitorPackNumber >= 2);

  const visibleCommercials = canShowInterlocutors
    ? "commercials" in structure
      ? structure.commercials.filter(
          (commercial) => commercial.visibleOnExhibitorPage
        )
      : []
    : [];

  const resolvedEventSchedules =
    eventSchedules ??
    (participations as MinimalEventForParticipation[]).flatMap(
      (participation) => participation.dates?.schedules ?? []
    );

  return (
    <div className="relative size-full overflow-auto">
      <Background
        customAppearance={customAppearance}
        getFileUrl={getFileUrl}
        isFixed={isBackgroundFixed}
      />
      <div className="relative z-10 w-full md:p-10 overflow-y-auto">
        {bannerPicture && canShowBanner ? (
          <Banner bannerPicture={bannerPicture} getFileUrl={getFileUrl} />
        ) : (
          <div className="h-5" />
        )}
        <div className="flex w-full flex-col gap-4 md:gap-6 md:px-10 px-3 max-md:py-8">
          <div className="relative z-20 -mt-12 flex w-full justify-between px-4 md:hidden">
            {onBackClick && <ReturnArrowButton onBackClick={onBackClick} />}
            {!isEmbedded && (
              <div className="flex gap-3">
                {onShareClick && <ShareButton onShareClick={onShareClick} />}
                {toggleLike && !isNullish(isLiked) && (
                  <LikeButton
                    isLiked={isLiked}
                    onLikeClick={() =>
                      actionGuard ? actionGuard(toggleLike) : toggleLike()
                    }
                  />
                )}
              </div>
            )}
          </div>
          <div
            className={cn("flex w-full flex-col gap-4 md:flex-row", {
              "md:-mt-4": bannerPicture && canShowBanner,
            })}
          >
            <div className="flex flex-col gap-4 grow">
              <StructureNameSection
                structure={structure}
                structureImageUrl={getFileUrl(picture?.key, picture?.type)}
                onConversationClick={onConversationClick}
                isLiked={isLiked}
                onShareClick={onShareClick}
                toggleLike={toggleLike}
                isEmbedded={isEmbedded}
                actionGuard={actionGuard}
                onSocialsClick={onSocialsClick}
              />
            </div>
            <div className="flex flex-col gap-3 pt-8 grow">
              <StructureContactSection
                structure={structure}
                onWebsiteClick={onWebsiteClick}
              />
            </div>
          </div>
          {description && (
            <div className="text-sm md:text-lg text-stone-900 font-semibold md:font-normal md:w-2/3 w-full md:-mt-2">
              {description}
            </div>
          )}
          {visibleCommercials.length > 0 && (
            <InterlocuteursSection commercials={visibleCommercials} />
          )}
          <StructureDetailsSection
            structure={structure}
            participations={participations}
            activePanel={activePanel}
            onPanelChange={onPanelChange}
            getFileUrl={getFileUrl}
            onOpenActivityList={onOpenActivityList}
            onEventCardClick={onEventCardClick}
            eventSchedules={resolvedEventSchedules}
            exhibitorPackNumber={exhibitorPackNumber}
          />
        </div>
      </div>
    </div>
  );
};

type VisibleCommercial = ExponentStructureType["commercials"][number];

const InterlocuteursSection: FC<{ commercials: VisibleCommercial[] }> = ({
  commercials,
}) => (
  <div className="flex flex-col gap-3">
    <h3 className="text-lg font-semibold text-stone-900">Interlocuteurs</h3>
    <div className="grid gap-3 md:grid-cols-2">
      {commercials.map((commercial) => {
        const commercialName = getName({
          firstName: commercial.user.firstName ?? commercial.firstName,
          lastName: commercial.user.lastName ?? commercial.lastName,
        });
        const commercialEmail = commercial.user.email ?? commercial.email;

        return (
          <div
            key={commercial.id}
            className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4"
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-50">
              <UserIcon size={20} className="text-primary-500" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-medium text-stone-900">{commercialName}</p>
              {commercial.jobTitle && (
                <p className="text-sm text-stone-500">{commercial.jobTitle}</p>
              )}
              {commercialEmail && (
                <div className="flex items-center gap-1.5 text-sm text-stone-600">
                  <EnvelopeSimpleIcon size={14} />
                  <a
                    href={`mailto:${commercialEmail}`}
                    className="hover:underline"
                  >
                    {commercialEmail}
                  </a>
                </div>
              )}
              {commercial.user.phoneNumber && (
                <div className="flex items-center gap-1.5 text-sm text-stone-600">
                  <PhoneIcon size={14} />
                  <a
                    href={`tel:${commercial.user.phoneNumber}`}
                    className="hover:underline"
                  >
                    {commercial.user.phoneNumber}
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const Banner = ({
  bannerPicture,
  getFileUrl,
}: {
  bannerPicture: File;
  getFileUrl: (key?: string, type?: FileType) => string;
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="w-full overflow-hidden relative">
      {!isLoaded && (
        <div className="absolute inset-0 animate-pulse bg-secondary-100 aspect-[3/1] md:aspect-[456/64] md:rounded-xl" />
      )}
      <img
        src={getFileUrl(bannerPicture.key, bannerPicture.type)}
        alt="Bannière"
        className={cn(
          "aspect-[3/1] w-full object-cover md:aspect-[456/64] md:rounded-xl scale-150 transition-opacity duration-300",
          isLoaded ? "opacity-100" : "opacity-0"
        )}
        draggable={false}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
};

const ReturnArrowButton = ({ onBackClick }: { onBackClick: () => void }) => (
  <button
    onClick={onBackClick}
    className="items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white p-2"
  >
    <ArrowLeftIcon size={20} className="text-primary-500" />
  </button>
);

const ShareButton = ({ onShareClick }: { onShareClick: () => void }) => (
  <div className="flex gap-2">
    <button
      onClick={onShareClick}
      className="items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white p-2"
    >
      <ShareFatIcon size={20} className="text-primary-500" />
    </button>
  </div>
);

const LikeButton = ({
  isLiked,
  onLikeClick,
}: {
  isLiked: boolean;
  onLikeClick: () => void;
}) => (
  <button
    onClick={onLikeClick}
    className="items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white p-2"
  >
    <HeartIcon
      size={20}
      weight={isLiked ? "fill" : "regular"}
      className="text-primary-500"
    />
  </button>
);

export const Background = ({
  customAppearance,
  getFileUrl,
  isFixed = true,
}: {
  customAppearance: CustomStructureAppearanceType;
  getFileUrl: (key?: string, type?: FileType) => string;
  isFixed?: boolean;
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const backgroundImageUrl = customAppearance?.backgroundPicture
    ? getFileUrl(customAppearance.backgroundPicture.key, "media")
    : undefined;

  useEffect(() => {
    if (!backgroundImageUrl) {
      setIsLoaded(true);
      return;
    }

    const img = new Image();

    img.onload = () => {
      setIsLoaded(true);
    };

    img.onerror = () => {
      setIsLoaded(true);
    };

    img.src = backgroundImageUrl;
  }, [backgroundImageUrl]);

  return (
    <>
      {!isLoaded && (
        <div
          className={cn(
            "z-0 size-full animate-pulse bg-secondary-100",
            isFixed ? "fixed" : "absolute"
          )}
        />
      )}
      <div
        className={cn(
          "z-0 size-full transition-opacity duration-500",
          isLoaded ? "opacity-100" : "opacity-0",
          isFixed ? "fixed" : "absolute"
        )}
        style={{
          backgroundColor: formatRGBColor(customAppearance.backgroundColor),
        }}
      />
      <div
        className={cn(
          "z-0 size-full transition-opacity duration-500 bg-cover bg-no-repeat bg-center",
          isLoaded ? "opacity-100" : "opacity-0",
          isFixed ? "fixed" : "absolute"
        )}
        style={{
          opacity: customAppearance.filterOpacity / 100,
          backgroundImage: backgroundImageUrl
            ? `url(${backgroundImageUrl})`
            : undefined,
        }}
      />
    </>
  );
};

export default StructureDetails;
